<script setup lang="ts">
import { useTemplateStore } from '@/store/templateStore';
import type { SideNavigationItemProps } from './AppSideNavigationItem.vue';

type SideNavigationButton = {
  id: string;
  shouldRender: boolean;
} & SideNavigationItemProps;

interface Props {
  templateId: string;
  fullMode: boolean;
  isMobile?: boolean;
}
const props = defineProps<Props>();
const mode = computed(() => (props.fullMode ? 'full' : 'minimal'));
const templateStore = useTemplateStore();
const template = templateStore.template(props.templateId);

const templateNavigationItems = computed((): SideNavigationButton[] => {
  return [
    {
      id: 'side-nav-opened-template',
      to: {
        name: 'ProjectMapTemplateMap',
        params: { templateId: props.templateId },
      },
      icon: 'Map',
      mode: mode.value,
      label: template.title,
      // Pages that hang off the map: entity pages and the map header's links
      activeRoutes: [
        'TemplateActivity',
        'TemplateMilestoneRoot',
        'TemplateSearchResults',
        'TemplateQualityChecks',
      ],
      shouldRender: !props.isMobile,
    },
    {
      id: 'side-nav-template-list',
      to: {
        name: 'TemplateListView',
        params: { templateId: props.templateId },
      },
      icon: 'List',
      mode: mode.value,
      label: $t('projectList.listView'),
      shouldRender: true,
    },
    {
      id: 'side-nav-template-documents',
      to: {
        name: 'TemplateDocuments',
        params: { templateId: props.templateId },
      },
      icon: 'File',
      mode: mode.value,
      label: $t('sideNavigation.projectDocuments'),
      shouldRender: !props.isMobile,
    },
    {
      id: 'side-nav-temp-links-and-tools',
      to: {
        name: 'TemplateLinksAndTools',
        params: { templateId: props.templateId },
      },
      icon: 'Link',
      mode: mode.value,
      label: $t('sideNavigation.linksAndTools'),
      shouldRender: !props.isMobile,
    },
    {
      id: 'side-nav-template-functions',
      to: {
        name: 'TemplateFunctions',
        params: { templateId: props.templateId },
      },
      icon: 'Users',
      mode: mode.value,
      label: $t('sideNavigation.functionsAndUsers'),
      shouldRender: !props.isMobile,
    },
    {
      id: 'side-nav-template-editors',
      to: {
        name: 'TemplateEditors',
        params: { templateId: props.templateId },
      },
      icon: 'Edit3',
      mode: mode.value,
      label: $t('sideNavigation.templateEditors'),
      shouldRender: !props.isMobile,
    },
  ];
});
</script>

<template>
  <template v-for="item in templateNavigationItems" :key="item.id">
    <AppSideNavigationItem
      v-if="item.shouldRender"
      :id="item.id"
      :to="item.to"
      :icon="item.icon"
      :mode="item.mode"
      :label="item.label"
      :active-routes="item.activeRoutes"
    />
  </template>
</template>
